import { useCounterCustomerChargeMaintenanceVM } from "presentation/hook/CounterCustomerCharge/useCounterCustomerChargeMaintenanceVM";
import { useEffect, useRef } from "react";

/**
 * Message shown by the browser in its built-in "Leave site?" dialog.
 * Modern browsers ignore custom text and render their own generic wording,
 * the value below is only kept for legacy support / consistency.
 */
const UNLOAD_WARNING_MESSAGE =
    "You have an unfinished counter customer charge payment. Leaving this page will roll back the payment.";

/**
 * Guards against an abnormal close (closing the tab / the browser / navigating away)
 * while the payment mode panel is open.
 *
 * Flow:
 *  1. `beforeunload` - asks the browser to prompt the user before leaving.
 *  2. If the user cancels, the page stays and nothing happens.
 *     If the user confirms, the document is destroyed and `pagehide` / `unload` fires,
 *     where the rollback (reject) request is sent through a keepalive request.
 *
 * Mounting this hook means "a payment is in progress", unmounting it (normal close / apply)
 * automatically removes the guard, so the rollback is never sent twice.
 */
export const usePaymentModeUnloadGuard = (docHdrId: number | null, isEnabled: boolean = true) => {
    const counterCustomerChargeVM = useCounterCustomerChargeMaintenanceVM();
    const docHdrIdRef = useRef<number | null>(docHdrId);
    const rollbackRef = useRef(counterCustomerChargeVM.rollbackPaymentModeOnUnload);

    useEffect(() => {
        docHdrIdRef.current = docHdrId;
    }, [docHdrId]);

    useEffect(() => {
        rollbackRef.current = counterCustomerChargeVM.rollbackPaymentModeOnUnload;
    }, [counterCustomerChargeVM]);

    useEffect(() => {
        if (!isEnabled || !docHdrId) {
            return;
        }

        let isRollbackSent = false;

        const rollback = () => {
            if (isRollbackSent) {
                return;
            }
            isRollbackSent = true;
            rollbackRef.current(docHdrIdRef.current);
        }

        const handleBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = UNLOAD_WARNING_MESSAGE;
            return UNLOAD_WARNING_MESSAGE;
        }

        // Only fired when the document is really gone, i.e. the user confirmed the prompt.
        const handlePageHide = (event: PageTransitionEvent) => {
            if (event.persisted) {
                return;
            }
            rollback();
        }

        // Fallback for browsers that do not fire pagehide on tab close.
        const handleUnload = () => {
            rollback();
        }

        window.addEventListener("beforeunload", handleBeforeUnload);
        window.addEventListener("pagehide", handlePageHide);
        window.addEventListener("unload", handleUnload);

        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
            window.removeEventListener("pagehide", handlePageHide);
            window.removeEventListener("unload", handleUnload);
        }
    }, [isEnabled, docHdrId]);
}

export default usePaymentModeUnloadGuard;
